import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Divider, Form, Modal, Space, Table } from 'antd';
import { useIntl } from 'react-intl';

import { DeleteIcon, EditIcon } from '@/components/_common';
import { ColumnsType } from 'antd/lib/table';
import {
  useDeleteTaskApplication,
  useTaskApplications,
  useUpdateTaskApplication,
} from '@/core/domain/operational/TaskAppllication/TaskApplication.hooks';
import {
  TaskApplication,
  TaskApplicationFormValues,
  UpdateTaskApplicationRequest,
} from '@/core/domain/operational/TaskAppllication/TaskApplication.types';
import { useDisclosure } from '@/hooks';
import ApplicationForm from '../ApplicationForm';
import { quantityFormatter } from '@/utils/number';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

interface Props {
  taskId: number;
}

const ApplicationList = ({ taskId }: Props) => {
  const intl = useIntl();

  const [form] = Form.useForm<TaskApplicationFormValues>();
  const [modalVisible, modalVisibleAction] = useDisclosure();
  const [aplicationData, setAplicationdata] = useState<TaskApplication>();

  const updateMutation = useUpdateTaskApplication();

  const { data, isFetching } = useTaskApplications(taskId);
  const deleteMutation = useDeleteTaskApplication();

  const handleDeleteTaskApplication = async (aplicationId: number) => {
    await deleteMutation.mutateAsync(aplicationId);
  };

  const handleCreateTaskApplication = async (
    formValues: TaskApplicationFormValues,
  ) => {
    if (aplicationData) {
      const obj: UpdateTaskApplicationRequest = {
        ...aplicationData,
        model: aplicationData.modelDTO,
        taskId: Number(taskId),
        applicationExecutionTime: formValues.applicationExecutionTime,
        id: aplicationData.id,
      };

      await updateMutation.mutateAsync(obj);

      modalVisibleAction.close();
    }
  };

  const columns: ColumnsType<TaskApplication> = [
    {
      title: intl.formatMessage({
        id: 'tasks.application.list.header.automaker',
      }),
      dataIndex: ['modelDTO', 'brand', 'name'],
      key: 'automaker',
    },
    {
      title: intl.formatMessage({ id: 'tasks.application.list.header.model' }),
      dataIndex: ['modelDTO', 'name'],
      key: 'model',
    },
    {
      title: intl.formatMessage({ id: 'tasks.application.list.header.time' }),
      dataIndex: 'applicationExecutionTime',
      key: 'applicationExecutionTime',
      render: (value) => {
        return value ? quantityFormatter(+value) : '-';
      },
    },
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      dataIndex: 'actions',
      key: 'action',
      render: (_, index) => (
        <Space>
          <EditIcon
            disabled={
              !hasPermissions([Authorities.REGISTER_TASK_ADD_APPLICATION])
            }
            iconProps={{
              onClick: async () => {
                form.setFieldValue('brandId', index.modelDTO.brand.brandId);

                form.setFieldsValue({
                  modelId: index.modelDTO.modelId,
                  nameModel: index.modelDTO.name,
                  applicationExecutionTime: index.applicationExecutionTime,
                });

                modalVisibleAction.toggle();

                setAplicationdata(index);
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleDeleteTaskApplication(index.id)}
            disabled={
              !hasPermissions([Authorities.REGISTER_TASK_DELETE_APPLICATION])
            }
          />
        </Space>
      ),
      fixed: 'right',
      align: 'right',
    },
  ];

  return (
    <div>
      <Helmet title="Aplicação" />

      <Table<TaskApplication>
        size="middle"
        columns={columns}
        dataSource={data?.data}
        rowKey={(row) => row.id}
        pagination={false}
        loading={isFetching}
      />

      <Modal
        open={modalVisible}
        onOk={() => handleCreateTaskApplication(form.getFieldsValue())}
        onCancel={modalVisibleAction.toggle}
        destroyOnClose
        title={intl.formatMessage({
          id: 'task.application.modal.addApplication',
        })}
      >
        <ApplicationForm type="update" form={form} />
      </Modal>
    </div>
  );
};

export default ApplicationList;
